import { debounce } from 'lodash';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useAfterPaintEffect, useAppSelector } from './app/hooks';
import { applyUrlParamsToState, getStateFromQueryString, writeStateToUrl } from './app/query-string';
import { useAppDispatch } from './app/store';
import { CalendarLayoutA, CalendarLayoutB, CalendarLayoutC } from './components/calendar-layout-options';
import { Loader } from '../../../global/components/loader';
import { Promo } from './components/promo';
import { ScrollToTop } from './components/scroll-to-top';
import { useGetEventsQuery, useGetFilterOptionsQuery } from './features/api/api';
import { setAppendMonths, setFilterBarHeight, setSingleMonth } from './features/calendar/calendar-slice';
import { CombinedFilterData, Month } from './helpers/common-types';
import { applyFilters, filterTypes } from './helpers/filter-helpers';
function App() {
    const dispatch = useAppDispatch();
    const layoutSetting = useAppSelector(state => state.calendar.layoutSetting);
    const calendarLoadingLabel = useAppSelector(state => state.settings.calendarLoadingLabel);
    const { calendarAppHeading, lastDate, productionSeasonId, gridViewModeButtonText, listViewModeButtonText, mainBody, loaderImageUrl } = useAppSelector(state => state.settings);
    const currentRange = useAppSelector(state => state.calendar.currentRange);
    const currentAppliedFilters = useAppSelector(state => state.calendar.currentAppliedFilters);
    const today = useAppSelector(state => state.calendar.todaysDate);
    const promoCode = useAppSelector(state => state.settings.promoCode);
    const currentDate = useAppSelector(state => state.calendar.currentDate);

    const eventsAreLoading = useAppSelector(state => state.calendar.eventsAreLoading);
    
    const { data: combinedFilterData = {}, isFetching: filtersFetching } = useGetFilterOptionsQuery({ startDate: today, endDate: lastDate, filterNames: filterTypes, promoCode: promoCode });
    const { data: events = [], isFetching, isLoading } = useGetEventsQuery({ ...currentRange, productionSeasonId: productionSeasonId });
    const filteredEvents = applyFilters(events, currentAppliedFilters);

    const isInitialLoad = useRef(true);

    useEffect(() => {
        // Handle initial URL params
        if (Object.keys(combinedFilterData).length > 0 && isInitialLoad.current) {
            applyUrlParamsToState(dispatch, combinedFilterData, layoutSetting);
            isInitialLoad.current = false;
            return;
        }

        // Handle subsequent state changes
        const newUrl = writeStateToUrl(currentDate, currentAppliedFilters, layoutSetting);
        if (newUrl !== window.location.href) {
            window.history.pushState({}, document.title, newUrl);
        }
    }, [dispatch, combinedFilterData, currentAppliedFilters, currentDate, layoutSetting]);

    // Handle browser back/forward
    useEffect(() => {
        const handlePopState = () => {
            applyUrlParamsToState(dispatch, combinedFilterData, layoutSetting);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [dispatch, combinedFilterData]);


    return (
        <div>
            <div className="ace-page-header">
                
                <div className="ace-layout ace-layout-8-4">
                    <h1>{calendarAppHeading}</h1>
                    <Promo />
                </div>
            </div>
            <div className="ace-page-gray">
                <div className="ace-calendar">

                    {
                        mainBody &&
                        <div className="rich-text" dangerouslySetInnerHTML={{ __html: mainBody }} />
                    }


                    {
                        isLoading || eventsAreLoading ? <Loader loaderImageUrl={loaderImageUrl} calendarLoadingLabel={calendarLoadingLabel} /> :
                            !!filteredEvents && !!combinedFilterData ?
                                <> 
                                    <CalendarLayout {...{ filteredEvents, layoutSetting, combinedFilterData }} />
                                </>

                                : null
                    }
                    <ScrollToTop />
                </div>
            </div>
        </div>
        
    )
}

export default App

const CalendarLayout = (props: { filteredEvents: Month[], layoutSetting: string, combinedFilterData: CombinedFilterData }) => {
    const { layoutSetting, ...layoutProps } = props;
    const dispatch = useAppDispatch();
    const { layoutBreakpoint } = useAppSelector(state => state.settings);
    const appendMonths = useAppSelector(state => state.calendar.appendMonths);
    const gridBreakpoint = useMediaQuery({ query: `(max-width: ${layoutBreakpoint})` });
    const filterBarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, []);

    useAfterPaintEffect(() => {
        setFilterHeight();
    }, [layoutSetting]);

    const setFilterHeight = () => {
        const filterHeight = filterBarRef?.current?.offsetHeight;
        if (filterHeight) {
            dispatch(setFilterBarHeight(filterHeight));
            document.documentElement.style.setProperty('--filter-bar-height', `${filterHeight}px`);
        }
    }

    // Set the type of range for this layout
    useLayoutEffect(() => {
        if (appendMonths) {
            if (layoutSetting == 'A') {
                dispatch(setSingleMonth());
            }
        }
        else {
            if (layoutSetting != 'A') {
                dispatch(setAppendMonths());
            }
        }
    }, [layoutSetting]);

    const handleResize = debounce(setFilterHeight, 250);

    const getLayout = (layout: string) => {
        switch (layout) {
            case 'A':
                // Layout A is special in that it shows one month at a time. Wait until the store settings have caught up before rendering this view.
                // If the component loads too early after switching from a multi-month view, it loads with all months initially and the scroll tracking gets out of sync.
                if (!appendMonths && layoutProps.filteredEvents.length == 1) {
                    return <CalendarLayoutA {...layoutProps} ref={filterBarRef} />
                }
                else {
                    return <Loader />
                }
            case 'B':
                return <CalendarLayoutB {...layoutProps} ref={filterBarRef} />
            case 'C': {
                if (gridBreakpoint) {
                    return <CalendarLayoutB {...layoutProps} ref={filterBarRef} />
                }
                else {
                    return <CalendarLayoutC {...layoutProps} ref={filterBarRef} />
                }
            }
            default:
                return <Loader />
        }
    }
    return <>{getLayout(layoutSetting)}</>;
}